import React, { useState } from 'react';

import { vestResolver } from '@hookform/resolvers/vest';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { flashMessage } from 'redux-flash';
import { create, enforce, test } from 'vest';

import { useEffectOnceWhen } from '../../../hooks/useEffectOnceWhen';
import { selectIsLoggedIn } from '../../user/selectors';
import { resetPassword, selectError, selectLoading, setToken } from '../slice';
import ResetPasswordForm from './ResetPasswordForm';

const validationSuite = create((data = {}) => {
  test('confirmNewPassword', 'Passwords must match', () => {
    enforce(data.confirmNewPassword).equals(data.newPassword);
  });

  test('newPassword', 'New password is required', () => {
    enforce(data.newPassword).isNotEmpty();
  });

  test('newPassword', 'Password must be at least 8 characters', () => {
    enforce(data.newPassword).longerThanOrEquals(8);
  });

  test('newPassword', 'Password must contain at least one number', () => {
    enforce(data.newPassword).matches(/\d/);
  });

  test('newPassword', 'Password must contain at least one uppercase letter', () => {
    enforce(data.newPassword).matches(/[A-Z]/);
  });

  test('newPassword', 'Password must contain at least one lowercase letter', () => {
    enforce(data.newPassword).matches(/[a-z]/);
  });

  test('newPassword', 'Password must contain at least one special character', () => {
    enforce(data.newPassword).matches(/[^A-Za-z0-9]/);
  });

  test('confirmNewPassword', 'Passwords must match', () => {
    enforce(data.confirmNewPassword).isNotEmpty();
  });
});

export default function ResetPasswordFormContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: vestResolver(validationSuite)
  });
  const pending = useSelector(selectLoading);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const apiError = useSelector(selectError);
  const [success, setSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const { token } = useParams();

  useEffectOnceWhen(
    () => {
      dispatch(setToken(token));
    },
    () => !!token
  );

  useEffectOnceWhen(() => {
    if (!isLoggedIn && !token) {
      navigate('/');
    }
  }, [!isLoggedIn, !token]);

  const onSubmit = (data) => {
    const { newPassword } = data;

    dispatch(resetPassword({ newPassword, token }))
      .unwrap()
      .then(() => {
        setSuccess(true);
        navigate('/');
        dispatch(
          flashMessage('Password updated successfully', { isError: false, timeout: 10_000 })
        );
      })
      .catch((error) => {
        if (error?.message?.includes('code 410')) {
          navigate('/forgot-password');
          dispatch(
            flashMessage('Password reset token has expired. Please request a new one.', {
              isError: true,
              timeout: false
            })
          );
        } else {
          console.error('Error resetting password:', error);
          setSaveError(error?.message);
        }
      });
  };

  return (
    <ResetPasswordForm
      isLoggedIn={isLoggedIn}
      register={register}
      errors={errors}
      error={saveError}
      handleSubmit={handleSubmit}
      pending={pending}
      onSubmit={onSubmit}
      success={success}
      apiError={apiError}
    />
  );
}
